import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { loadLocalPlayer, saveLocalPlayer, clearLocalPlayer, loadLocalFamily, saveLocalFamily } from "@/lib/storage";
import { getPlayer, updateProgress as apiUpdateProgress, updateMode as apiUpdateMode } from "@/lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const [player, setPlayer] = useState(null);
  const [familyId, setFamilyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const local = loadLocalPlayer();
    setFamilyId(loadLocalFamily());
    if (!local?.id) { setLoading(false); return; }
    getPlayer(local.id).then((p) => {
      setPlayer(p);
      saveLocalPlayer(p);
    }).catch(() => {
      setPlayer(local);
    }).finally(() => setLoading(false));
  }, []);

  const setAndPersist = useCallback((p) => {
    setPlayer(p);
    if (p) saveLocalPlayer(p);
  }, []);

  const persistFamily = useCallback((id) => {
    setFamilyId(id);
    if (id) saveLocalFamily(id);
  }, []);

  const switchToChild = useCallback(async (childId) => {
    try {
      const p = await getPlayer(childId);
      setAndPersist(p);
      return p;
    } catch (e) { return null; }
  }, [setAndPersist]);

  const commitProgress = useCallback(async (payload) => {
    if (!player?.id) return null;
    try {
      const updated = await apiUpdateProgress(player.id, payload);
      setAndPersist(updated);
      return updated;
    } catch (e) {
      // Offline fallback: update locally
      const p = { ...player };
      p.memory_shards = (p.memory_shards || 0) + (payload.memory_shards_gained || 0);
      p.playtime_seconds = (p.playtime_seconds || 0) + (payload.playtime_seconds_added || 0);
      p.hints_used = (p.hints_used || 0) + (payload.hints_used_added || 0);
      if (payload.level_id && !p.levels_completed?.includes(payload.level_id)) {
        p.levels_completed = [...(p.levels_completed || []), payload.level_id];
        p.puzzles_solved = (p.puzzles_solved || 0) + 1;
      }
      if (payload.relic_unlocked && !p.relics_unlocked?.includes(payload.relic_unlocked)) {
        p.relics_unlocked = [...(p.relics_unlocked || []), payload.relic_unlocked];
      }
      if (payload.grimoire_page_unlocked && !p.grimoire_pages?.includes(payload.grimoire_page_unlocked)) {
        p.grimoire_pages = [...(p.grimoire_pages || []), payload.grimoire_page_unlocked];
      }
      setAndPersist(p);
      return p;
    }
  }, [player, setAndPersist]);

  const switchMode = useCallback(async (mode) => {
    if (!player?.id) return;
    try {
      const updated = await apiUpdateMode(player.id, mode);
      setAndPersist(updated);
    } catch (e) {
      setAndPersist({ ...player, mode });
    }
  }, [player, setAndPersist]);

  const logout = useCallback(() => {
    clearLocalPlayer();
    setPlayer(null);
  }, []);

  return (
    <PlayerContext.Provider value={{ player, setPlayer: setAndPersist, loading, commitProgress, switchMode, logout, familyId, setFamilyId: persistFamily, switchToChild }}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used inside PlayerProvider");
  return ctx;
}
